import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Button, Modal, Search } from '@indipia/ui-core';
import { useAuthStore } from '@indipia/shared';
import { photoApi } from '../api/photoApi';
import type { CustomerSiteItem } from '../api/photoApi';

// 현장 카드 정렬 — 이름 가나다순 (ClassifySiteMain.tsx의 sortByName과 동일 기준으로 통일).
// BE(/customer/sites)는 생성일순으로 내려오는 Flutter 공용 프록시라 여기서 클라이언트단 재정렬로 맞춘다.
const sortByName = <T extends { siteName: string }>(a: T, b: T): number => a.siteName.localeCompare(b.siteName, 'ko');

type ModalView = 'type-select' | 'existing';

interface ConfirmedSite {
  siteId: number;
  siteName: string;
  address: string | null;
}

interface Props {
  onClose: () => void;
  onConfirm: (sites: ConfirmedSite[]) => void;
  onNewSiteRequest?: () => void;
  existingSiteIds?: Set<number>;
}

const PIN_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);

function ModalHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '20px 20px 14px' }}>
      <div style={{ width: 38, height: 38, borderRadius: 'var(--radius-md)', background: 'var(--gt-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {PIN_ICON}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-0.4px' }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--shell-hint)', marginTop: 2, letterSpacing: '-0.1px' }}>업로드 사진을 배정할 현장을 등록하세요</div>
      </div>
      <button
        onClick={onClose}
        aria-label="닫기"
        style={{ width: 28, height: 28, borderRadius: 'var(--radius-full)', border: 'none', background: 'var(--shell-skeleton)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0 }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <Button
      variant="ghost"
      onClick={onBack}
      style={{ height: 'auto', padding: '0 20px 12px', color: 'var(--shell-hint)', fontSize: 'var(--type-caption-size)' }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
      돌아가기
    </Button>
  );
}

function ConfirmButton({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  return (
    <div style={{ padding: '12px 20px 20px' }}>
      <Button variant="primary" size="lg" className="w-full" disabled={disabled} onClick={onClick}>
        {label}
      </Button>
    </div>
  );
}

const STATUS_STYLE_MAP: Record<string, { bg: string; color: string }> = {
  CONSTRUCT: { bg: 'var(--gt-subtle)', color: 'var(--gt-primary)' },
  QUOTE:     { bg: 'var(--gt-subtle)', color: 'var(--color-info)' },
  DONE:      { bg: 'var(--shell-skeleton)', color: 'var(--shell-hint)' },
};

function StatusBadge({ status, label }: { status: string | null; label: string | null }) {
  if (!status || !label) return null;
  const s = STATUS_STYLE_MAP[status] ?? { bg: 'var(--shell-skeleton)', color: 'var(--shell-hint)' };
  return (
    <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 9px', borderRadius: 'var(--radius-sm)', background: s.bg, color: s.color, flexShrink: 0, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

/* ── View 1: 기존/신규 선택 ── */
function TypeSelectView({ onSelect, onNewSiteRequest }: { onSelect: (v: ModalView) => void; onNewSiteRequest?: () => void }) {
  const items = [
    {
      onClick: () => onSelect('existing'),
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
      label: '기존 현장',
      desc: '이미 등록된 현장에\n사진을 배정합니다',
    },
    {
      onClick: () => onNewSiteRequest?.(),
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" />
        </svg>
      ),
      label: '신규 현장',
      desc: '새로운 고객·현장 정보를\n직접 입력합니다',
    },
  ];

  return (
    <div style={{ padding: '0 20px 20px' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface-2)', marginBottom: 14, letterSpacing: '-0.2px' }}>
        어떤 현장으로 등록하시겠어요?
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {items.map(item => (
          <Button
            key={item.label}
            variant="ghost"
            onClick={item.onClick}
            style={{ height: 'auto', flexDirection: 'column', gap: 10, padding: '20px 12px', border: '1.5px solid var(--shell-border)', borderRadius: 'var(--radius-lg)', background: 'var(--shell-surface)', transition: 'border-color .15s,background .15s' }}
            onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--gt-primary)'; e.currentTarget.style.background = 'var(--gt-subtle)'; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--shell-border)'; e.currentTarget.style.background = 'var(--shell-surface)'; }}
          >
            <div style={{ width: 52, height: 52, borderRadius: 'var(--radius-lg)', background: 'var(--gt-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {item.icon}
            </div>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-0.3px', marginBottom: 4 }}>{item.label}</div>
              <div style={{ fontSize: 12, color: 'var(--shell-hint)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{item.desc}</div>
            </div>
          </Button>
        ))}
      </div>
    </div>
  );
}

/* ── View 2: 기존 현장 선택 (멀티셀렉트) ── */
function ExistingSelectView({
  onBack,
  onConfirm,
  existingSiteIds,
}: {
  onBack: () => void;
  onConfirm: (sites: ConfirmedSite[]) => void;
  existingSiteIds: Set<number>;
}) {
  const [keyword, setKeyword] = useState('');
  const [sites, setSites] = useState<CustomerSiteItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const user = useAuthStore.getState().user;
    if (!user) return;

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    photoApi.searchCustomerSites(keyword, ctrl.signal)
      .then(res => { setSites([...res.data.data.items].sort(sortByName)); })
      .catch(() => { if (!ctrl.signal.aborted) toast.error('현장 목록을 불러오는데 실패했습니다'); })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });

    return () => ctrl.abort();
  }, [keyword]);

  const toggleSite = (siteId: number) => {
    if (existingSiteIds.has(siteId)) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(siteId) ? next.delete(siteId) : next.add(siteId);
      return next;
    });
  };

  const handleConfirm = () => {
    const selected = sites
      .filter(s => selectedIds.has(s.siteId))
      .map(s => ({ siteId: s.siteId, siteName: s.siteName, address: s.siteAddress }));
    if (!selected.length) return;
    onConfirm(selected);
  };

  const selectedCount = selectedIds.size;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <BackButton onBack={onBack} />
      {/* 검색 입력 */}
      <div style={{ padding: '0 20px 10px', flexShrink: 0 }}>
        <Search
          autoFocus
          value={keyword}
          onChange={setKeyword}
          onClear={() => setKeyword('')}
          placeholder="현장명·고객명·주소 검색"
        />
      </div>
      {/* 선택 건수 표시 */}
      {selectedCount > 0 && (
        <div style={{ margin: '0 20px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--gt-primary)' }}>{selectedCount}개 현장 선택됨</span>
          <Button
            variant="ghost"
            onClick={() => setSelectedIds(new Set())}
            style={{ height: 'auto', padding: 0, color: 'var(--shell-hint)', fontSize: 12 }}
          >
            선택 해제
          </Button>
        </div>
      )}
      {/* 결과 목록 */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', margin: '0 20px', border: '1.5px solid var(--shell-border)', borderRadius: 'var(--radius-md)' }}>
        {loading ? (
          <div style={{ padding: '28px 0', textAlign: 'center', fontSize: 12, color: 'var(--shell-hint)' }}>불러오는 중...</div>
        ) : sites.length === 0 ? (
          <div style={{ padding: '28px 0', textAlign: 'center', fontSize: 12, color: 'var(--shell-hint)' }}>
            {keyword ? '검색 결과가 없습니다' : '등록된 현장이 없습니다'}
          </div>
        ) : (
          sites.map((site, idx) => {
            const selected = selectedIds.has(site.siteId);
            const alreadyAdded = existingSiteIds.has(site.siteId);
            return (
              <div
                key={site.siteId}
                onClick={() => toggleSite(site.siteId)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', borderBottom: idx < sites.length - 1 ? '1px solid var(--shell-border)' : 'none', cursor: alreadyAdded ? 'not-allowed' : 'pointer', background: selected ? 'var(--gt-subtle)' : 'var(--shell-surface)', opacity: alreadyAdded ? 0.5 : 1, transition: 'background .12s' }}
              >
                <div style={{
                  width: 20, height: 20, borderRadius: 'var(--radius-sm)',
                  border: `2px solid ${alreadyAdded ? 'var(--shell-border)' : selected ? 'var(--gt-primary)' : 'var(--shell-border)'}`,
                  background: alreadyAdded ? 'var(--shell-skeleton)' : selected ? 'var(--gt-primary)' : 'var(--shell-surface)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all .12s',
                }}>
                  {alreadyAdded
                    ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="3" strokeLinecap="round"><line x1="5" y1="19" x2="19" y2="5" /></svg>
                    : selected && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-0.2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {site.siteName}
                    </span>
                    {alreadyAdded
                      ? <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 9px', borderRadius: 'var(--radius-sm)', background: 'var(--shell-skeleton)', color: 'var(--shell-hint)', flexShrink: 0, whiteSpace: 'nowrap' }}>이미 추가됨</span>
                      : <StatusBadge status={site.siteStatus} label={site.siteStatusName ?? site.siteStatus} />
                    }
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--shell-hint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[site.customerName, site.siteAddress].filter(Boolean).join(' · ')}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <ConfirmButton
        label={selectedCount > 0 ? `${selectedCount}개 현장 선택 완료` : '현장을 선택하세요'}
        disabled={selectedCount === 0}
        onClick={handleConfirm}
      />
    </div>
  );
}

/* ── 메인 모달 ── */
export function SiteSelectModal({ onClose, onConfirm, onNewSiteRequest, existingSiteIds }: Props) {
  const [view, setView] = useState<ModalView>('type-select');

  const title = view === 'type-select' ? '현장 선택/생성' : '기존 현장 선택';

  return (
    <Modal open onClose={onClose}>
      <div style={{ margin: '-4px -18px -14px', height: 'min(580px, 90vh)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <ModalHeader title={title} onClose={onClose} />
        <div style={{ height: 1, background: 'var(--shell-border)', flexShrink: 0 }} />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', paddingTop: 16 }}>
          {view === 'type-select' && <TypeSelectView onSelect={setView} onNewSiteRequest={onNewSiteRequest} />}
          {view === 'existing' && <ExistingSelectView onBack={() => setView('type-select')} onConfirm={onConfirm} existingSiteIds={existingSiteIds ?? new Set()} />}
        </div>
      </div>
    </Modal>
  );
}
